import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Table } from '@backstage/core-components';
import { getEntityRelations, useEntity } from '@backstage/plugin-catalog-react';
import { RELATION_PARENT_OF } from '@backstage/catalog-model';
import { Box, Button, Tooltip, Typography } from '@material-ui/core';
import { assetCandidateRefs, toGroupRef } from '../../utils/entityRefHelpers';
import { useEntityExistenceTracker } from '../../lib/useEntityExistenceTracker';
import { Link as RouterLink } from 'react-router-dom';
import { securityInsightsApiRef } from '../../api/apiRef';
import type { SecurityIssue, SingleViewFilters } from '../../api/types';
import { SingleViewFilterPanel } from '../Filters/FilterPanel';
import { buildFindingColumns, dupColumn } from '../findingColumns';
import { GroupBySummaryPanel } from '../GroupBySummaryPanel';
import { GroupByControl } from '../GroupByControl';
import { useTaxonomyLabels } from '../../hooks/useTaxonomyLabels';

type TeamGroupByMode =
  | 'team'
  | 'asset'
  | 'pillarCategory'
  | 'issueType'
  | 'systemInstance'
  | 'cve';

const TEAM_GROUP_BY_OPTIONS: { value: TeamGroupByMode; label: string }[] = [
  { value: 'team', label: 'Team / Experience' },
  { value: 'asset', label: 'Asset' },
  { value: 'pillarCategory', label: 'Pillar / Category' },
  { value: 'issueType', label: 'Tool' },
  { value: 'systemInstance', label: 'System / Deployed Instance' },
  { value: 'cve', label: 'CVE / Rule' },
];

type PortalLinkState =
  | { kind: 'team'; team: string }
  | { kind: 'experience'; experience: string }
  | { kind: 'unavailable' };

/**
 * The security portal models a Backstage parent Group as an "experience",
 * not as a team — there is no single `team` value that could represent it.
 * A leaf group (no children) keeps linking by team, matching what the
 * portal actually has. A parent group only gets a working link when its
 * display name matches a known experience; otherwise there's nothing in
 * the portal to link to.
 */
function resolvePortalLinkState(
  hasChildren: boolean,
  teamName: string,
  displayName: string,
  experiences: string[] | null,
): PortalLinkState {
  if (!hasChildren) return { kind: 'team', team: teamName };
  if (experiences?.includes(displayName)) {
    return { kind: 'experience', experience: displayName };
  }
  return { kind: 'unavailable' };
}

type FetchIssuesQuery = {
  page: number;
  pageSize: number;
  orderBy?: { field?: string };
  orderDirection?: string;
};

type FetchIssuesResult = {
  data: SecurityIssue[];
  page: number;
  totalCount: number;
};

/**
 * Only rendered when `groupBy === 'team'`. Keeping `useTaxonomyLabels` here
 * (rather than in the parent) means its `getPillars`/`getCategories` fetch
 * is skipped entirely in the other group-by modes, where
 * `GroupBySummaryPanel` already fetches the same vocab itself.
 */
const TeamFindingsTable = ({
  existingRefs,
  deduplicate,
  totalCount,
  tableKey,
  fetchIssues,
}: {
  existingRefs: ReadonlySet<string>;
  deduplicate: boolean | undefined;
  totalCount: number;
  tableKey: number;
  fetchIssues: (query: FetchIssuesQuery) => Promise<FetchIssuesResult>;
}) => {
  const { pillarLabels, categoryLabels } = useTaxonomyLabels();
  const issueColumns = useMemo(() => {
    const base = buildFindingColumns(existingRefs, pillarLabels, categoryLabels);
    return deduplicate ? [...base, dupColumn] : base;
  }, [existingRefs, deduplicate, pillarLabels, categoryLabels]);

  return (
    <>
      <Typography variant="subtitle2">
        Security Issues ({totalCount.toLocaleString()} total)
      </Typography>
      <Table
        key={tableKey}
        options={{
          paging: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          emptyRowsWhenPaging: false,
          search: false,
          padding: 'dense',
          toolbar: false,
        }}
        columns={issueColumns}
        data={fetchIssues}
      />
    </>
  );
};

export const EntityGroupSecurityTab = (): JSX.Element => {
  const { entity } = useEntity();
  const api = useApi(securityInsightsApiRef);
  const teamName = entity.metadata.name;
  // Sub-team dropdown options (PAT-606 Option B) come straight from the
  // catalog's already-computed parentOf relations — no extra network call,
  // same source `hasChildren` used before this was a list rather than a bool.
  const subTeamOptions = useMemo(
    () => getEntityRelations(entity, RELATION_PARENT_OF, { kind: 'group' }).map(r => r.name).sort(),
    [entity],
  );
  const hasChildren = subTeamOptions.length > 0;
  const displayName = entity.metadata.title?.trim() || entity.metadata.name;

  const [experiences, setExperiences] = useState<string[] | null>(null);
  useEffect(() => {
    if (!hasChildren) return undefined;
    let cancelled = false;
    api.getExperiences().then(
      result => {
        if (!cancelled) setExperiences(result);
      },
      () => {
        if (!cancelled) setExperiences([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, hasChildren]);
  const portalLinkState = resolvePortalLinkState(hasChildren, teamName, displayName, experiences);

  const { existing: existingRefs, addRefs } = useEntityExistenceTracker();

  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<SingleViewFilters | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const filtersRef = useRef<SingleViewFilters | null>(null);
  const [groupBy, setGroupBy] = useState<TeamGroupByMode>('team');

  const handleFiltersChange = useCallback((filters: SingleViewFilters) => {
    filtersRef.current = filters;
    setCurrentFilters(filters);
    setTableKey(k => k + 1);
  }, []);

  const issuesVersionRef = useRef(0);
  const fetchIssues = useCallback(
    async (query: FetchIssuesQuery): Promise<FetchIssuesResult> => {
      if (!filtersRef.current) return { data: [], page: 0, totalCount: 0 };
      const version = ++issuesVersionRef.current;
      const result = await api.getSingleView({
        ...filtersRef.current,
        expandTeamHierarchy: true,
        page: query.page,
        pageSize: query.pageSize,
        orderBy: query.orderBy?.field,
        orderDirection: (query.orderDirection as 'asc' | 'desc') || undefined,
      });
      if (version !== issuesVersionRef.current) {
        return { data: [], page: query.page, totalCount: 0 };
      }
      setTotalCount(result.totalCount);
      const refs: Array<string | null> = [];
      for (const row of result.data) {
        refs.push(toGroupRef(row.team ?? ''));
        refs.push(...assetCandidateRefs(row.asset ?? '', row.issue_type));
      }
      addRefs(refs);
      return { data: result.data, page: query.page, totalCount: result.totalCount };
    },
    [api, addRefs],
  );

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        {portalLinkState.kind === 'unavailable' ? (
          <Tooltip title="Not available for this group">
            <span>
              <Button variant="outlined" disabled>
                Open in Security Portal
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            component={RouterLink}
            to={
              portalLinkState.kind === 'experience'
                ? `/security-insights?experience=${encodeURIComponent(portalLinkState.experience)}`
                : `/security-insights?team=${encodeURIComponent(portalLinkState.team)}`
            }
          >
            Open in Security Portal
          </Button>
        )}
      </Box>
      <Box mb={2}>
        <SingleViewFilterPanel
          lockedTeam={teamName}
          subTeamOptions={subTeamOptions}
          showSystemFilters
          searchLabel="Search"
          searchPlaceholder="e.g. asset name, team, CVE, rule ID, issue title..."
          onFiltersChange={handleFiltersChange}
        />
      </Box>
      <Box mb={2}>
        <GroupByControl value={groupBy} onChange={setGroupBy} options={TEAM_GROUP_BY_OPTIONS} />
      </Box>
      {groupBy === 'team' ? (
        <TeamFindingsTable
          existingRefs={existingRefs}
          deduplicate={currentFilters?.deduplicate}
          totalCount={totalCount}
          tableKey={tableKey}
          fetchIssues={fetchIssues}
        />
      ) : (
        currentFilters && (
          <GroupBySummaryPanel
            filters={{ ...currentFilters, expandTeamHierarchy: true }}
            modes={['asset', 'pillarCategory', 'issueType', 'systemInstance', 'cve']}
            activeMode={groupBy}
            existingRefs={existingRefs}
            addRefs={addRefs}
          />
        )
      )}
    </Box>
  );
};
